import { Bell, UserCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { useAppData } from './useAppData';

type DesktopNavbarProps = {
  app: ReturnType<typeof useAppData>;
  onOpenShortcuts(): void;
};

export function DesktopNavbar({ app, onOpenShortcuts }: DesktopNavbarProps) {
  const userLabel = app.user?.username || app.user?.email || app.user?.id || 'Sign in';
  const [accountOpen, setAccountOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!accountOpen) return undefined;

    function handlePointerDown(event: PointerEvent) {
      if (accountMenuRef.current?.contains(event.target as Node | null)) return;
      setAccountOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setAccountOpen(false);
    }

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [accountOpen]);

  return (
    <header className="desktop-shell-navbar">
      <div className="desktop-shell-brand">
        <strong>Chartify</strong>
      </div>
      <div className="desktop-shell-actions">
        {app.canUseAlarms ? (
          <button
            type="button"
            onClick={() => {
              void app.openAlarmForSymbolName(app.chartSymbol, app.quotes[app.chartSymbol]?.price);
            }}
          >
            <Bell size={16} strokeWidth={2} />
            <span>Alarm</span>
          </button>
        ) : null}
        <div ref={accountMenuRef} className="desktop-account-menu">
          <button
            type="button"
            aria-haspopup={app.user ? 'menu' : undefined}
            aria-expanded={app.user ? accountOpen : undefined}
            onClick={() => {
              if (!app.user) {
                window.location.assign('/signin');
                return;
              }
              setAccountOpen((open) => !open);
            }}
          >
            <UserCircle size={17} strokeWidth={2} />
            <span>{userLabel}</span>
          </button>
          {app.user && accountOpen ? (
            <div className="desktop-account-dropdown" role="menu">
              <div className="desktop-account-dropdown-label">
                <strong>{userLabel}</strong>
                {app.user?.email && app.user.email !== userLabel ? <span>{app.user.email}</span> : null}
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAccountOpen(false);
                  onOpenShortcuts();
                }}
              >
                Shortcuts
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAccountOpen(false);
                  void app.signOut();
                }}
              >
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
